import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Home as HomeIcon, HardHat, Wallet, ShieldCheck, LifeBuoy,
  Menu, Bell, ArrowLeftRight,
} from "lucide-react";

import { api, clearToken, getToken } from "./lib/api";
import { Login } from "./screens/Login";
import { Home } from "./screens/Home";
import { Drawer } from "./screens/Drawer";
import { ProjectSheet } from "./screens/Project";
import { PaymentsSheet } from "./screens/Payments";
import { DesignsSheet } from "./screens/Designs";
import { InspirationsSheet } from "./screens/Inspirations";
import { CareSheet } from "./screens/Care";
import { SupportSheet } from "./screens/Support";
import { UpdatesSheet } from "./screens/Updates";
import { ReferSheet } from "./screens/Refer";
import { Veeha } from "./veeha/Veeha";
import { Mark } from "./brand/Mark";
import { Logo } from "./brand/Logo";
import { VeehaGlyph, VeehaDisc } from "./brand/Veeha";
import { Err } from "./ui";

const NAME_KEY = "dleaps_portal_client";

/* Four tabs plus Veeha in the middle. Help lives in the rail and the drawer:
   a fifth tab for something opened once a month costs a slot that Veeha —
   opened every visit — has a better claim to. */
const TABS = [
  ["home", "Home", HomeIcon],
  ["project", "Project", HardHat],
  ["payments", "Payments", Wallet],
  ["care", "Care", ShieldCheck],
];

export function App() {
  const [signedIn, setSignedIn] = useState(!!getToken());
  const [clientName, setClientName] = useState(() => localStorage.getItem(NAME_KEY) || "");

  const signOut = useCallback(() => {
    clearToken();
    localStorage.removeItem(NAME_KEY);
    setSignedIn(false);
  }, []);

  const signIn = useCallback((_token, name) => {
    localStorage.setItem(NAME_KEY, name || "");
    setClientName(name || "");
    setSignedIn(true);
  }, []);

  if (!signedIn) return <Login onLogin={signIn} />;
  return <Portal clientName={clientName} signOut={signOut} />;
}

function Portal({ clientName, signOut }) {
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState(null);
  const [studio, setStudio] = useState(null);
  const [fatal, setFatal] = useState("");
  const [retry, setRetry] = useState(0);

  const [sheet, setSheet] = useState(null);      // null | "project" | "payments" | …
  const [drawer, setDrawer] = useState(false);
  const [veeha, setVeeha] = useState(false);
  // A seed question, set by whatever opened her. Cleared on close so the next
  // open starts from her greeting rather than replaying the last question.
  const [veehaSeed, setVeehaSeed] = useState("");
  const [unread, setUnread] = useState(0);

  /* ---- projects + studio identity ---------------------------------------- */
  useEffect(() => {
    let dead = false;
    setFatal("");
    (async () => {
      try {
        const list = await api.get("/portal/projects");
        if (dead) return;
        setProjects(list);
        if (list.length === 1) setPid(list[0].id);
      } catch (e) {
        if (dead) return;
        if (e.status === 401) signOut();
        else setFatal(e.message || "Something went wrong.");
        return;
      }
      try {
        const s = await api.get("/portal/studio");
        if (!dead) setStudio(s);
      } catch { /* the studio block is decoration; never block the app on it */ }
    })();
    return () => { dead = true; };
  }, [retry, signOut]);

  /* ---- unread updates ---------------------------------------------------- */
  useEffect(() => {
    if (!pid) return;
    let dead = false;
    const poll = () =>
      api.get(`/portal/notifications?project_id=${pid}`)
        .then((r) => { if (!dead) setUnread(r.unread || 0); })
        .catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    return () => { dead = true; clearInterval(t); };
  }, [pid, sheet]);

  const project = useMemo(
    () => (projects || []).find((p) => p.id === pid) || null,
    [projects, pid]
  );

  const ask = useCallback((question) => { setVeehaSeed(question || ""); setVeeha(true); }, []);

  const ctx = useMemo(
    () => ({ pid, project, projects, studio, clientName, open: setSheet, signOut, ask }),
    [pid, project, projects, studio, clientName, signOut, ask]
  );

  /* ---- gates ------------------------------------------------------------- */
  if (fatal) {
    return (
      <div className="app">
        <div className="center" style={{ paddingTop: "22vh" }}>
          <Mark size={54} />
          <p style={{ maxWidth: 340 }}>{fatal}</p>
          <button className="btn primary" onClick={() => setRetry((n) => n + 1)}>Try again</button>
          <button className="btn ghost" onClick={signOut}>Sign out</button>
        </div>
      </div>
    );
  }

  if (projects === null) {
    return (
      <div className="app">
        <div className="center" style={{ paddingTop: "26vh" }}>
          <Mark size={54} />
          <p className="muted">Opening your project…</p>
        </div>
      </div>
    );
  }

  if (!projects.length) {
    return (
      <div className="app">
        <div className="center" style={{ paddingTop: "22vh" }}>
          <Mark size={54} />
          <p>No active project on your account yet.</p>
          <p className="muted" style={{ fontSize: ".85rem", maxWidth: 320 }}>
            Once your studio opens a project against this number it appears here.
          </p>
          <button className="btn" onClick={signOut}>Sign out</button>
        </div>
      </div>
    );
  }

  /* ---- project picker ---------------------------------------------------- */
  if (!pid) {
    return (
      <div className="app">
        <AppBar onMenu={null} onBell={null} onAsk={() => setVeeha(true)} unread={0} />
        <div className="wrap" style={{ paddingTop: 8 }}>
          <p className="sec-title" style={{ padding: "0 0 10px" }}>Choose a project</p>
          {projects.map((p) => (
            <button className="row glass" key={p.id} onClick={() => setPid(p.id)}>
              <span className="row-ic"><HardHat size={17} /></span>
              <span className="row-txt">
                <b>{p.title}</b>
                <small className="mono">{p.project_code}</small>
              </span>
              <span className="pill">{p.status_title || "In progress"}</span>
            </button>
          ))}
          <button className="btn ghost block" style={{ marginTop: 10 }} onClick={signOut}>Sign out</button>
        </div>
        {veeha && <Veeha page="picker" onClose={() => setVeeha(false)} />}
      </div>
    );
  }

  const tab = sheet && TABS.some(([k]) => k === sheet) ? sheet : "home";

  return (
    <div className="app">
      <AppBar
        onMenu={() => setDrawer(true)}
        onBell={() => setSheet("updates")}
        unread={unread}
      />

      <Home ctx={ctx} unread={unread} />

      {/* ---- sheets --------------------------------------------------------- */}
      {sheet === "project" && <ProjectSheet ctx={ctx} onClose={() => setSheet(null)} />}
      {sheet === "payments" && <PaymentsSheet ctx={ctx} onClose={() => setSheet(null)} />}
      {sheet === "designs" && <DesignsSheet ctx={ctx} onClose={() => setSheet(null)} />}
      {sheet === "ideas" && <InspirationsSheet ctx={ctx} onClose={() => setSheet(null)} />}
      {sheet === "care" && <CareSheet ctx={ctx} onClose={() => setSheet(null)} />}
      {sheet === "support" && <SupportSheet ctx={ctx} onClose={() => setSheet(null)} />}
      {sheet === "updates" && <UpdatesSheet ctx={ctx} onClose={() => setSheet(null)} />}
      {sheet === "refer" && <ReferSheet ctx={ctx} onClose={() => setSheet(null)} />}

      {drawer && (
        <Drawer
          ctx={ctx}
          onClose={() => setDrawer(false)}
          onPick={(k) => { setDrawer(false); if (k === "switch") setPid(null); else setSheet(k); }}
          onAsk={() => { setDrawer(false); setVeeha(true); }}
        />
      )}

      {veeha && <Veeha page={sheet || "home"} seed={veehaSeed} onClose={() => { setVeeha(false); setVeehaSeed(""); }} />}

      {/* ---- tab bar, with Veeha in the middle ------------------------------ */}
      <nav className="nav" aria-label="Sections">
        {TABS.slice(0, 2).map(([key, label, Icon]) => (
          <NavTab key={key} tabKey={key} label={label} Icon={Icon} on={tab === key}
                  unread={key === "home" ? unread : 0} onPick={setSheet} />
        ))}

        <span className="veeha-slot">
          <button className="nav-veeha" onClick={() => setVeeha(true)} aria-label="Ask Veeha, D'leaps AI">
            <VeehaDisc size={54} />
          </button>
          <span className="lbl">Veeha</span>
        </span>

        {TABS.slice(2).map(([key, label, Icon]) => (
          <NavTab key={key} tabKey={key} label={label} Icon={Icon} on={tab === key}
                  unread={0} onPick={setSheet} />
        ))}
      </nav>
    </div>
  );
}

function NavTab({ tabKey, label, Icon, on, unread, onPick }) {
  return (
    <button
      className={on ? "on" : ""}
      aria-current={on ? "page" : undefined}
      onClick={() => onPick(tabKey === "home" ? null : tabKey)}
    >
      <Icon size={19} strokeWidth={1.7} />
      {label}
      {unread > 0 && <span className="nav-badge">{unread > 9 ? "9+" : unread}</span>}
    </button>
  );
}

function AppBar({ onMenu, onBell, onAsk, unread }) {
  return (
    <header className="bar">
      {onMenu && (
        <button className="bar-btn" onClick={onMenu} aria-label="Menu">
          <Menu size={19} />
        </button>
      )}
      <span className="bar-brand">
        <Logo height={26} />
      </span>
      {onBell && (
        <button className="bar-btn" onClick={onBell} aria-label={`Updates${unread ? `, ${unread} unread` : ""}`}>
          <Bell size={18} />
          {unread > 0 && <span className="bar-dot" />}
        </button>
      )}
      {onAsk && (
        <button className="bar-btn" onClick={onAsk} aria-label="Ask Veeha, D'leaps AI">
          <VeehaGlyph size={19} />
        </button>
      )}
    </header>
  );
}

export { ArrowLeftRight };
